import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ClaimReviewPane from './ClaimReviewPane.mjs';
import DocumentPane from '../../components/DocumentPane.mjs';
import { isInfected } from '../../utils/scanStatus.mjs';
import { exportPerDiemClaimToExcel, exportExpenseClaimToExcel } from '../../utils/claimExcel.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import ClarificationDrawer from '../../clarifications/components/ClarificationDrawer.mjs';
import QuestionBadge from '../../clarifications/components/QuestionBadge.mjs';
import { useOpenQuestions } from '../../clarifications/hook/useOpenQuestions.mjs';
import { withOpenQuestionsConfirm, isCancelled } from '../../clarifications/util/confirmOpenQuestions.mjs';

/**
 * The guided claim-by-claim review walkthrough.
 *
 * Left half: the claim under review, fixed for the whole of that claim.
 * Right half: one supporting document at a time - a per-diem claim's trip
 * documents, or an expense claim's line-item receipts followed by its claim-level
 * documents. Paging right moves the highlight on the left to the matching line item.
 *
 * "Mark as audited/reviewed" is available as soon as the claim loads. It used to
 * unlock only on the LAST document, which on an expense claim with a dozen
 * receipts meant paging through every one - and, because the way on to the next
 * claim only appeared there too, a claim could not be passed over without it.
 * What the reviewer looks at is their judgement; the receipts are all one click
 * away, and a line item opens its own receipt when clicked.
 *
 * Used in two places, which is why the caller supplies `fetchBundle` and the
 * action handlers rather than this component knowing about reports:
 *   - a monthly report's claims  (flag as the problem path, approve at the end)
 *   - the auditor's Claims Pre-Approval list (ask-to-resubmit as the problem
 *     path, no report-level action)
 *
 * Either way a reviewer can ASK instead: a clarification question to someone
 * below them, in a side drawer, without the claim leaving anyone's hands.
 * Marking a claim with a question still open asks for confirmation first -
 * `onMark(claimId, claimType, { acknowledgeOpenQuestions })`.
 */

const ROLE_COPY = {
  auditor: { verb: 'Audit', marked: 'Audited', markAction: 'Mark as audited' },
  regional_director: { verb: 'Review', marked: 'Reviewed', markAction: 'Mark as reviewed' },
  global_director: { verb: 'Review', marked: 'Reviewed', markAction: 'Mark as reviewed' },
  finance_director: { verb: 'Review', marked: 'Reviewed', markAction: 'Mark as reviewed' },
};

const fmtCurrency = (amount) => {
  const n = typeof amount === 'number'
    ? amount
    : parseFloat(String(amount ?? '').replace(/[^0-9.-]/g, ''));
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(Number.isFinite(n) ? n : 0);
};

const cacheKey = (claim) => `${claim.claimType}:${claim.claimId}`;

const MassReviewOverlay = ({
  title,
  subtitle,
  claims,
  role,
  loadBundle,
  initiallyDone,
  onClose,
  onMark,
  onFlag,
  onReturn,
  onApprove,
  onSendBack,
  approving = false,
  // The report being walked, if any - scopes the question badges to it.
  reportId = null,
}) => {
  const copy = ROLE_COPY[role] || ROLE_COPY.auditor;
  const { confirm } = useDialog();

  // Clarification questions on the claims being walked, for the badges, and
  // the drawer for asking and reading them.
  const { forClaim } = useOpenQuestions({ reportId });
  const [askOpen, setAskOpen] = useState(false);

  // Snapshot the list on open. The pending-audit list REMOVES a claim as soon as
  // it is audited or returned, so walking the live array would shift every index
  // after each action and silently skip claims. Work from the snapshot and track
  // completion locally instead.
  const [claimList] = useState(() => [...claims]);

  // Claims completed during this sitting, on top of whatever was already done
  // when we opened. Keyed on (claimType, claimId) - never on a row id, which the
  // report's destroy-and-recreate save regenerates.
  const [doneKeys, setDoneKeys] = useState(() => new Set());
  const wasDone = useCallback(
    (claim) => !!claim && (doneKeys.has(cacheKey(claim)) || !!initiallyDone?.(claim)),
    [doneKeys, initiallyDone]
  );

  // Resume where the reviewer left off rather than restarting at claim 1.
  const firstPending = claimList.findIndex(c => !initiallyDone?.(c));
  const [claimIndex, setClaimIndex] = useState(firstPending === -1 ? 0 : firstPending);
  const [showSummary, setShowSummary] = useState(firstPending === -1);
  const [supportIndex, setSupportIndex] = useState(0);

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [exporting, setExporting] = useState(false);

  // One reason box, serving flag (report mode) or ask-to-resubmit (pending mode).
  const [reasonOpen, setReasonOpen] = useState(null); // 'flag' | 'return' | null
  const [reasonText, setReasonText] = useState('');
  const [reasonSaving, setReasonSaving] = useState(false);

  // Phones only: there is no room for the claim and its document side by side,
  // so one is shown at a time behind a Details | Documents toggle. Ignored at
  // md and up, where both panes are always visible.
  const [mobilePane, setMobilePane] = useState('details'); // 'details' | 'document'

  // Bundles are memoised for the session so Prev/Next between claims is instant.
  // Signed URLs last 7 days, comfortably longer than any review sitting.
  const cache = useRef(new Map());

  const currentClaim = claimList[claimIndex];
  const claimKey = currentClaim ? cacheKey(currentClaim) : null;
  const supports = bundle?.supports || [];
  // Line items whose receipt failed the virus scan, so the left pane can flag
  // them without the reviewer having to page to each document to find out.
  const infectedItemIds = useMemo(() => new Set(
    (bundle?.supports || [])
      .filter((sp) => sp.itemId && isInfected(sp))
      .map((sp) => String(sp.itemId))
  ), [bundle]);
  /**
   * Show the document belonging to one line item.
   *
   * Paired with the clickable rows in ClaimReviewPane: the highlight already
   * flowed document -> row, this is the missing row -> document direction.
   */
  /**
   * The claim as a spreadsheet, built here from the review bundle.
   *
   * No round trip: the bundle already carries every line with its per-line PTAE,
   * which is exactly what the claim page's own Excel export consumes. Auditors
   * were leaving the walkthrough, opening the claim, downloading, and coming
   * back - for every claim in the batch.
   */
  const downloadClaimExcel = useCallback(async () => {
    const claim = bundle?.claim;
    if (!claim) return;
    setExporting(true);
    try {
      const lines = claim.dailyDetails || claim.items || [];
      const linePtae = {};
      for (const line of lines) {
        if (line?.ptae) linePtae[String(line.id)] = line.ptae;
      }
      const allocation = { linePtae, ptae: claim.ptae || null };
      const isPerDiem = /perdiem/i.test(String(claim.claimType || currentClaim?.claimType || ''));
      if (isPerDiem) await exportPerDiemClaimToExcel(claim, allocation);
      else await exportExpenseClaimToExcel(claim, allocation);
    } catch (err) {
      setActionError(err.message || 'Could not build the spreadsheet');
    } finally {
      setExporting(false);
    }
  }, [bundle, currentClaim]);

  const showItemDocument = useCallback((itemId) => {
    const i = (bundle?.supports || []).findIndex(
      (sp) => sp.itemId && String(sp.itemId) === String(itemId)
    );
    if (i >= 0) setSupportIndex(i);
  }, [bundle]);
  const currentSupport = supports[supportIndex] || null;
  const atLastSupport = supportIndex >= supports.length - 1;
  const hasSupports = supports.length > 0;

  // Held in a ref so an inline `loadBundle={(c) => ...}` from the caller cannot
  // change fetchBundle's identity on every render, which would re-run the load
  // effect and throw the reviewer back to document 1 mid-claim.
  const loadBundleRef = useRef(loadBundle);
  loadBundleRef.current = loadBundle;

  const fetchBundle = useCallback(async (claim) => {
    const key = cacheKey(claim);
    if (cache.current.has(key)) return cache.current.get(key);
    const data = await loadBundleRef.current(claim);
    cache.current.set(key, data);
    return data;
  }, []);

  // Load the current claim, then warm the next one so advancing feels immediate.
  // Keyed on claimKey, not the claim object: only actually moving to a different
  // claim should reset the document pager.
  useEffect(() => {
    if (showSummary || !claimKey) return;
    const claim = claimList[claimIndex];
    if (!claim) return;
    let cancelled = false;

    setLoading(true);
    setLoadError(null);
    setActionError(null);
    setSupportIndex(0);

    fetchBundle(claim)
      .then(data => {
        if (cancelled) return;
        setBundle(data);
        setLoading(false);
        const next = claimList[claimIndex + 1];
        if (next) fetchBundle(next).catch(() => {});
      })
      .catch(err => {
        if (cancelled) return;
        setLoadError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [claimIndex, claimKey, showSummary, fetchBundle, claimList]);

  const goToClaim = (index) => {
    setClaimIndex(index);
    setShowSummary(false);
  };

  // Memoised because the → key handler depends on it; without this the keydown
  // listener would be torn down and re-added on every render.
  const advance = useCallback(() => {
    setClaimIndex(prev => {
      if (prev + 1 < claimList.length) return prev + 1;
      setShowSummary(true);
      return prev;
    });
  }, [claimList.length]);

  const markDone = (claim) => setDoneKeys(prev => new Set(prev).add(cacheKey(claim)));

  const handleMark = async () => {
    if (marking) return;
    setMarking(true);
    setActionError(null);
    try {
      // An open question on the claim warns first; carrying on resends with
      // the acknowledgement.
      await withOpenQuestionsConfirm(confirm, (acknowledgeOpenQuestions) =>
        onMark(currentClaim.claimId, currentClaim.claimType, { acknowledgeOpenQuestions }));
      markDone(currentClaim);
      advance();
    } catch (err) {
      // A claim that is not admin-approved cannot be audited - show why rather
      // than failing silently, so the reviewer knows to skip it. Backing out
      // of the open-questions warning is not a failure.
      if (!isCancelled(err)) setActionError(err.message);
    } finally {
      setMarking(false);
    }
  };

  const handleReasonSubmit = async () => {
    setReasonSaving(true);
    setActionError(null);
    try {
      if (reasonOpen === 'return') {
        // Returning hands the claim back to the admin, so it is finished as far
        // as this sitting is concerned - mark it off and move on.
        await onReturn(currentClaim.claimId, currentClaim.claimType, reasonText);
        markDone(currentClaim);
        setReasonOpen(null);
        setReasonText('');
        advance();
        return;
      }

      const result = await onFlag(currentClaim.claimId, currentClaim.claimType, reasonText);
      // Keep the pane's flag list in step without a refetch.
      const key = cacheKey(currentClaim);
      const cached = cache.current.get(key);
      if (cached && result?.data) {
        const updated = {
          ...cached,
          reportClaim: { ...cached.reportClaim, ...result.data },
        };
        cache.current.set(key, updated);
        setBundle(updated);
      }
      setReasonOpen(null);
      setReasonText('');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setReasonSaving(false);
    }
  };

  // Arrow keys page the documents; Esc leaves. Skipped while the reason box or
  // the questions drawer is open, so typing cannot navigate away from the
  // claim it is about (the drawer closes itself on Esc).
  useEffect(() => {
    const onKey = (e) => {
      if (askOpen) return;
      if (reasonOpen) {
        if (e.key === 'Escape') setReasonOpen(null);
        return;
      }
      if (e.key === 'Escape') { onClose(); return; }
      if (showSummary) return;
      if (e.key === 'ArrowRight') {
        // Past the last document the arrow carries on to the next claim, so the
        // keyboard path does not dead-end where the buttons no longer do.
        if (supportIndex < supports.length - 1) setSupportIndex(supportIndex + 1);
        else advance();
      }
      if (e.key === 'ArrowLeft' && supportIndex > 0) {
        setSupportIndex(supportIndex - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [askOpen, reasonOpen, showSummary, supportIndex, supports.length, onClose, advance]);

  const doneCount = claimList.filter(wasDone).length;
  const flaggedCount = claimList.filter(c => c.flagged).length;
  const allDone = doneCount === claimList.length;

  // Summed from the claims themselves rather than taken from a report, since the
  // pending-audit list has no report to read a total off. The field differs by
  // source: report rows carry allocatedAmount/total, pending claims totalAmount.
  const totalAmount = claimList.reduce((sum, c) => {
    const raw = c.allocatedAmount ?? c.total ?? c.totalAmount ?? 0;
    const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  // ---------- Completion preview ----------
  if (showSummary) {
    return (
      <div className="fixed inset-0 z-[9000] bg-gray-100 flex flex-col">
        <div className="bg-gray-900 text-white px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-bold">{title} - {copy.verb} Summary</h2>
            <p className="text-sm text-gray-400">
              {subtitle} · {claimList.length} claims
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-5xl mx-auto">
            {/* Flags only exist on report rows, so that tile is dropped in the
                pending-audit walkthrough rather than always reading zero.
                Phones: two tiles a row, the total full width underneath. */}
            <div className={`grid ${onFlag ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'} gap-3 sm:gap-4 mb-4 sm:mb-6`}>
              <div className="bg-white rounded-xl shadow p-3 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                  {copy.marked}
                </p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 tabular-nums">
                  {doneCount} <span className="text-base text-gray-400">/ {claimList.length}</span>
                </p>
              </div>
              {onFlag && (
                <div className="bg-white rounded-xl shadow p-3 sm:p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Flagged</p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-600 mt-1 tabular-nums">{flaggedCount}</p>
                </div>
              )}
              <div className={`bg-white rounded-xl shadow p-3 sm:p-4 ${onFlag ? 'col-span-2 sm:col-span-1' : ''}`}>
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Total</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 tabular-nums">
                  {fmtCurrency(totalAmount)}
                </p>
              </div>
            </div>

            {!allDone && (
              <div className="mb-4 sm:mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  {claimList.length - doneCount} claim
                  {claimList.length - doneCount !== 1 ? 's have' : ' has'} not been {copy.marked.toLowerCase()} yet.
                  You can still approve the report, or click a claim below to go back to it.
                </p>
              </div>
            )}

            <div className="bg-white rounded-xl shadow overflow-hidden">
              {/* Below md each claim becomes a labelled card (index.css
                  RESPONSIVE TABLES); the table is unchanged from md up. */}
              <table className="responsive-table w-full">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">#</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Total</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">{copy.marked}</th>
                    {onFlag && <th className="px-4 py-3 text-left text-sm font-semibold">Flag</th>}
                    <th className="px-4 py-3 text-left text-sm font-semibold">Questions</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {claimList.map((claim, i) => (
                    <tr key={`${claim.claimType}-${claim.claimId}`} className="hover:bg-gray-50">
                      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-500">{i + 1}</td>
                      <td data-cell="title" className="px-4 py-3 text-sm font-medium text-gray-900 break-words">{claim.userName}</td>
                      <td data-label="Type" className="px-4 py-3 text-sm text-gray-600">
                        {claim.claimType === 'PerDiemClaim' ? 'Per Diem' : 'Expense'}
                      </td>
                      <td data-label="Total" className="px-4 py-3 text-sm text-right text-gray-900 tabular-nums">
                        {fmtCurrency(claim.allocatedAmount ?? claim.total ?? claim.totalAmount)}
                      </td>
                      <td data-label={copy.marked} className="px-4 py-3">
                        {wasDone(claim) ? (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">
                            ✓ {copy.marked}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full">
                            Pending
                          </span>
                        )}
                      </td>
                      {onFlag && (
                        <td data-label="Flag" className="px-4 py-3">
                          {claim.flagged ? (
                            <span
                              className="px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full"
                              title={claim.flagReason || ''}
                            >
Flagged
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      <td data-label="Questions" className="px-4 py-3">
                        {forClaim(claim.claimType, claim.claimId).length > 0
                          ? <QuestionBadge threads={forClaim(claim.claimType, claim.claimId)} />
                          : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td data-cell="action" className="px-4 py-3 text-right">
                        <button
                          onClick={() => goToClaim(i)}
                          className="text-sm text-stanford-red hover:underline font-medium md:font-normal border border-gray-300 md:border-0 rounded-lg md:rounded-none"
                        >
                          Revisit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Phones: the primary action (Approve, last in the DOM) sits full width
            on top, Done / Send Back share the row under it, and the way back to
            the claims is a quiet link at the bottom. */}
        <div className="review-summary-footer bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2 flex-shrink-0">
          <button
            onClick={() => goToClaim(0)}
            className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
          >
            ‹ Back to first claim
          </button>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
            <button
              onClick={onClose}
              className={`min-h-[44px] sm:min-h-0 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors ${onSendBack ? '' : 'col-span-2'}`}
            >
              Done
            </button>
            {onSendBack && (
              <button
                onClick={onSendBack}
                disabled={approving}
                className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 leading-tight sm:leading-normal bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                Send Back for Revision
              </button>
            )}
            {onApprove && (
              <button
                onClick={onApprove}
                disabled={approving}
                className="col-span-2 order-first sm:order-none min-h-[44px] sm:min-h-0 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
              >
                {approving ? 'Processing…' : '✓ Approve Report'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Split-screen review ----------
  return (
    <div className="fixed inset-0 z-[9000] bg-gray-100 flex flex-col">
      {/* Top bar - claim-level progress.
          Phones: a 4-column grid - title beside Exit on the first row, the
          claim controls four across under it (the button group is
          display:contents there so its buttons join this grid). From md up it
          is the original single flex row. */}
      <div className="bg-gray-900 text-white px-3 py-2 md:px-6 md:py-3 grid grid-cols-4 md:flex items-center justify-between gap-2 md:gap-4 flex-shrink-0">
        <div className="min-w-0 col-span-3">
          <h2 className="text-base font-bold truncate">
            {title} · Claim {claimIndex + 1} of {claimList.length}
          </h2>
          <p className="text-xs text-gray-400 flex flex-wrap items-center gap-2">
            <span>{subtitle} · {doneCount} {copy.marked.toLowerCase()}</span>
            {currentClaim && (
              <QuestionBadge
                threads={forClaim(currentClaim.claimType, currentClaim.claimId)}
                onClick={() => setAskOpen(true)}
              />
            )}
          </p>
        </div>

        <div className="contents md:flex items-center gap-3 flex-shrink-0">
          <div className="hidden md:block w-40 bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 bg-emerald-500 rounded-full transition-all duration-300"
              style={{ width: `${claimList.length ? (doneCount / claimList.length) * 100 : 0}%` }}
            />
          </div>
          <button
            onClick={() => setClaimIndex(Math.max(0, claimIndex - 1))}
            disabled={claimIndex === 0}
            className="min-h-[44px] md:min-h-0 px-1 md:px-3 py-1.5 text-xs leading-tight md:leading-4 bg-gray-700 hover:bg-gray-600 rounded transition-colors disabled:opacity-40"
          >
            ‹ Prev claim
          </button>
          <button
            onClick={advance}
            className="min-h-[44px] md:min-h-0 px-1 md:px-3 py-1.5 text-xs leading-tight md:leading-4 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
          >
            Skip claim ›
          </button>
          {/* The claim as a spreadsheet, with its PTAE breakdown - the thing
              auditors were leaving the walkthrough to fetch from the claim page. */}
          <button
            onClick={downloadClaimExcel}
            disabled={!bundle?.claim || exporting}
            className="min-h-[44px] md:min-h-0 px-1 md:px-3 py-1.5 text-xs leading-tight md:leading-4 bg-gray-700 hover:bg-gray-600 rounded transition-colors disabled:opacity-40"
            title="Download this claim and its PTAE allocation as Excel"
          >
            {exporting ? 'Preparing…' : 'Excel'}
          </button>
          <button
            onClick={() => setShowSummary(true)}
            className="min-h-[44px] md:min-h-0 px-1 md:px-3 py-1.5 text-xs leading-tight md:leading-4 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
          >
            Summary
          </button>
          <button
            onClick={onClose}
            className="col-start-4 row-start-1 min-h-[44px] md:min-h-0 px-1 md:px-3 py-1.5 text-xs leading-tight md:leading-4 bg-stanford-red hover:bg-stanford-light-red rounded transition-colors"
          >
            Exit
          </button>
        </div>
      </div>

      {actionError && (
        <div className="bg-red-50 border-b border-red-200 px-4 md:px-6 py-2.5 flex-shrink-0">
          <p className="text-sm text-red-800 break-words">{actionError}</p>
        </div>
      )}

      {/* Phones: one pane at a time. A line item that is tapped jumps straight
          to its receipt, the same as clicking it beside the document on desktop. */}
      <div
        role="tablist"
        aria-label="Show the claim or its documents"
        className="md:hidden grid grid-cols-2 gap-1 p-1 bg-white border-b border-gray-300 flex-shrink-0"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === 'details'}
          onClick={() => setMobilePane('details')}
          className={`min-h-[44px] rounded-lg text-sm font-semibold transition-colors ${
            mobilePane === 'details' ? 'bg-stanford-red text-white shadow' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === 'document'}
          onClick={() => setMobilePane('document')}
          className={`min-h-[44px] rounded-lg text-sm font-semibold transition-colors ${
            mobilePane === 'document' ? 'bg-stanford-red text-white shadow' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Documents
          {/* The space keeps the accessible name "Documents 1/4", not "Documents1/4". */}
          {!loading && ' '}
          {!loading && (
            <span className="ml-1 tabular-nums font-normal opacity-80">
              {hasSupports ? `${supportIndex + 1}/${supports.length}` : '0'}
            </span>
          )}
        </button>
      </div>

      {/* 50/50 split from md up; stacked panes behind the toggle above on phones */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* LEFT - the claim, fixed for this claim's whole review */}
        <div
          className={`${mobilePane === 'details' ? 'flex-1' : 'hidden md:block'} md:flex-initial md:w-1/2 min-h-0 overflow-y-auto overscroll-contain bg-white md:border-r border-gray-300`}
        >
          {loading && (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-3" />
              <p className="text-gray-500 text-sm">Loading claim…</p>
            </div>
          )}
          {loadError && (
            <div className="p-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800">Could not load this claim</p>
                <p className="text-sm text-red-700 mt-1">{loadError}</p>
              </div>
            </div>
          )}
          {!loading && !loadError && (
            <ClaimReviewPane
              claim={bundle?.claim}
              reportClaim={bundle?.reportClaim}
              activeItemId={currentSupport?.itemId}
              infectedItemIds={infectedItemIds}
              onSelectItem={(itemId) => { showItemDocument(itemId); setMobilePane('document'); }}
            />
          )}
        </div>

        {/* RIGHT - one supporting document at a time. On phones the action bar
            below stays in view on both tabs; only the document area toggles. */}
        <div
          className={`${mobilePane === 'document' ? 'flex-1' : 'flex-shrink-0'} md:flex-initial md:w-1/2 flex flex-col min-h-0`}
        >
          <div className={`${mobilePane === 'document' ? '' : 'hidden md:block'} flex-1 min-h-0`}>
            {loading ? (
              <div className="flex items-center justify-center h-full bg-gray-900">
                <div className="w-8 h-8 border-4 border-gray-700 border-t-stanford-red rounded-full animate-spin" />
              </div>
            ) : !hasSupports ? (
              <div className="flex flex-col items-center justify-center h-full bg-gray-900 text-center px-8">
                <p className="text-gray-200 font-medium">No supporting documents</p>
                <p className="text-gray-400 text-sm mt-1">
                  {bundle?.claim?.claimType === 'PerDiemClaim'
                    ? 'This per diem claim has no trip support attached.'
                    : 'This expense claim has no line-item receipts or supporting documents attached.'}
                </p>
              </div>
            ) : (
              <DocumentPane
                url={currentSupport?.url}
                fileType={currentSupport?.fileType}
                fileName={currentSupport?.fileName}
                signError={currentSupport?.signError}
                infected={isInfected(currentSupport)}
                title={currentSupport?.label}
                subtitle={
                  currentSupport?.kind === 'lineItem'
                    ? [
                        currentSupport.expenditureDescription,
                        currentSupport.usdEquivalent != null
                          ? fmtCurrency(currentSupport.usdEquivalent)
                          : null,
                      ].filter(Boolean).join(' · ')
                    : currentSupport?.kind === 'claimDoc'
                      ? `Claim-level document${currentSupport.description ? ` · ${currentSupport.description}` : ''}`
                      : currentSupport?.description || 'Trip support'
                }
              />
            )}
          </div>

          {/* Document pager + claim actions.
              Phones: a thumb-reach bottom bar - the pager row, then the claim
              actions full width under it, clear of the home indicator. */}
          <div className="review-action-bar bg-white border-t border-gray-300 px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-4 md:py-3 flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSupportIndex(supportIndex - 1)}
                disabled={supportIndex === 0 || !hasSupports}
                className="flex-1 md:flex-initial min-h-[44px] md:min-h-0 px-2 md:px-3 py-1.5 text-xs md:text-sm leading-tight md:leading-5 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
              >
                ‹ Prev document
              </button>
              <span className="text-xs md:text-sm text-gray-600 tabular-nums whitespace-nowrap min-w-[3rem] md:min-w-[70px] text-center">
                {hasSupports ? `${supportIndex + 1} of ${supports.length}` : '0 of 0'}
              </span>
              {/* Next DOCUMENT - only while there is one. Styled as a live
                  button: in the same pale grey as the disabled Prev beside it,
                  it read as disabled too. */}
              {hasSupports && !atLastSupport && (
                <button
                  onClick={() => setSupportIndex(supportIndex + 1)}
                  className="flex-1 md:flex-initial min-h-[44px] md:min-h-0 px-2 md:px-3 py-1.5 text-xs md:text-sm leading-tight md:leading-5 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Next document ›
                </button>
              )}
              {/* Next CLAIM - always there. It used to appear only on the last
                  document, so an expense claim with twelve receipts had to be
                  paged through before the reviewer could move on at all. */}
              <button
                onClick={advance}
                title="Go to the next claim"
                className="flex-1 md:flex-initial min-h-[44px] md:min-h-0 px-2 md:px-3 py-1.5 text-xs md:text-sm leading-tight md:leading-5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Next claim ›
              </button>
            </div>

            <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
              {/* The problem path differs only in name. Inside a report the
                  control is "Flag"; outside one it is "Ask to Resubmit". Both
                  now drop the claim to under review and hand it to the admin -
                  a flag used to be advisory, which left the admin holding a
                  complaint about a claim they were locked out of editing. */}
              {onFlag && (
                <button
                  onClick={() => {
                    setReasonText(bundle?.reportClaim?.reviewFlags?.[
                      role === 'finance_director' ? 'global_director' : role
                    ]?.reason || '');
                    setReasonOpen('flag');
                  }}
                  disabled={loading || !bundle?.canMark}
                  className="flex-1 md:flex-initial min-h-[44px] md:min-h-0 px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-40"
                >
Flag
                </button>
              )}

              {onReturn && (
                <button
                  onClick={() => { setReasonText(''); setReasonOpen('return'); }}
                  disabled={loading || !bundle?.canMark}
                  title="Send this claim back to the admin to revise and resubmit"
                  className="flex-1 md:flex-initial min-h-[44px] md:min-h-0 px-3 py-1.5 text-sm leading-tight md:leading-5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-40"
                >
                  ↩ Ask to Resubmit
                </button>
              )}

              {/* Ask instead of sending back: the claim stays where it is while
                  someone below answers. Not gated on canMark - a reviewer may
                  ask at any stage; the drawer says so if they cannot. */}
              <button
                onClick={() => setAskOpen(true)}
                disabled={loading || !currentClaim}
                title="Ask a question about this claim without sending it back"
                className="flex-1 md:flex-initial min-h-[44px] md:min-h-0 px-3 py-1.5 text-sm leading-tight md:leading-5 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-40"
              >
                ? Ask
              </button>

              {/* A disabled green button with no explanation reads as a bug.
                  The reason the server gave is shown rather than hovered for. */}
              {!loading && !wasDone(currentClaim) && !bundle?.canMark && (
                <span className="order-first md:order-none w-full md:w-auto text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 md:max-w-[280px]">
                  {bundle?.markBlockedReason || 'This report is not at your review stage'}
                </span>
              )}

              {wasDone(currentClaim) ? (
                <span className="flex-[2] md:flex-initial flex md:block items-center justify-center min-h-[44px] md:min-h-0 px-4 py-1.5 text-sm font-semibold bg-emerald-100 text-emerald-800 rounded-lg">
                  ✓ {copy.marked}
                </span>
              ) : (
                <button
                  onClick={handleMark}
                  disabled={loading || marking || !bundle?.canMark}
                  title={
                    !bundle?.canMark
                      ? (bundle?.markBlockedReason || 'This report is not at your review stage')
                      : ''
                  }
                  className="flex-[2] md:flex-initial min-h-[44px] md:min-h-0 px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-40 font-medium"
                >
                  {marking ? 'Saving…' : `✓ ${copy.markAction}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Questions about the current claim - asked, answered and resolved
          here, without leaving the walkthrough. */}
      {askOpen && currentClaim && (
        <ClarificationDrawer
          title="Questions about this claim"
          subtitle={`${currentClaim.userName || 'Claim'} · ${currentClaim.claimType === 'PerDiemClaim' ? 'Per Diem' : 'Expense'}`}
          claimType={currentClaim.claimType}
          claimId={currentClaim.claimId}
          onClose={() => setAskOpen(false)}
        />
      )}

      {/* Reason box - serves both the flag and the ask-to-resubmit paths */}
      {reasonOpen && (() => {
        const returning = reasonOpen === 'return';
        // A resubmit request MUST carry a reason (the backend rejects a blank one,
        // and the admin needs to know what to fix). A blank flag is meaningful -
        // it clears your own flag.
        const blocked = returning && !reasonText.trim();
        return (
          <div className="fixed inset-0 z-[9100] bg-black/50 flex items-center justify-center p-3 sm:p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)] flex flex-col">
              <div className="px-4 py-2 sm:px-6 sm:py-4 border-b border-gray-200 flex items-center justify-between gap-3 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">
                  {returning ? 'Ask for a resubmit' : 'Flag this claim'}
                </h3>
                <button
                  onClick={() => setReasonOpen(null)}
                  aria-label="Close"
                  className="flex sm:block items-center justify-center w-11 h-11 -mr-2 sm:w-auto sm:h-auto sm:mr-0 text-gray-400 hover:text-gray-600 text-xl leading-none"
                >
                  &times;
                </button>
              </div>
              <div className="p-4 sm:p-6 overflow-y-auto min-h-0">
                <p className="text-sm text-gray-600 mb-3">
                  {returning ? (
                    <>
                      This sends <strong>{currentClaim?.userName}</strong>'s claim back to their
                      region's admin for revision. The claim returns to <em>under review</em>, any
                      earlier audit stamp is cleared, and the admin is emailed your reason.
                    </>
                  ) : (
                    <>
                      This sends <strong>{currentClaim?.userName}</strong>'s claim back to their
                      region's admin. The claim returns to <em>under review</em> so they can edit
                      it, and the report cannot be approved until the flag is cleared or the whole
                      report is sent back. Your flag is recorded against your role and does not
                      replace anyone else's.
                    </>
                  )}
                </p>
                <textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  rows={4}
                  autoFocus
                  placeholder={returning
                    ? 'What does the admin need to correct?'
                    : 'What does the admin need to correct on this claim?'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stanford-red"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {returning
                    ? 'A reason is required - it is sent to the admin.'
                    : 'The reason is what the admin sees. Leave it blank and save to clear '
                      + 'your own flag, which approves the claim again.'}
                </p>
              </div>
              <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={() => setReasonOpen(null)}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReasonSubmit}
                  disabled={reasonSaving || blocked}
                  title={blocked ? 'Enter a reason first' : ''}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {reasonSaving
                    ? 'Saving…'
                    : returning
                      ? 'Send Back to Admin'
                      : reasonText.trim() ? 'Flag and Send Back' : 'Clear Flag'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MassReviewOverlay;
